import React, {useRef, useEffect} from 'react';
import PropTypes from 'prop-types';

import styled from '@emotion/styled';
import gsap from 'gsap';

const SVG = styled.svg`
  position: absolute;
  &#kac {
    height: ${({size}) => size && `${size.height}%`};
    width: ${({size}) => size && `${size.width}%`};
    left: calc(340 / 1080 * 100%);
    top: calc(160 / 1540 * 100%);
  }
  &#zai {
    height: ${({size}) => size && `${size.height}%`};
    width: ${({size}) => size && `${size.width}%`};
    left: calc(360 / 1080 * 100%);
    top: calc(450 / 1540 * 100%);
  }
  &#disi {
    height: ${({size}) => size && `${size.height}%`};
    width: ${({size}) => size && `${size.width}%`};
    left: calc(550 / 1080 * 100%);
    top: calc(160 / 1540 * 100%);
  }
  &#zara {
    height: ${({size}) => size && `${size.height}%`};
    width: ${({size}) => size && `${size.width}%`};
    left: calc(335 / 1080 * 100%);
    top: calc(530 / 1540 * 100%);
  }

  .circle {
    fill: #ffffff;
  }

  .conveyor {
    transition: opacity 200ms ease-in;
    cursor: pointer;
  }

  &.active {
    .conveyor {
      opacity: 0.25;
      &.is-active {
        opacity: 1;
        transition: opacity 200ms ease-out;
      }
    }
  }
`;

const SVGLayer = ({data: {entries}, active, setActiveProject}) => {
  const mapOriginalWidth = 1080;
  const mapOriginalHeight = 1540;

  const kacPoints = entries.find(({id}) => id === 'kac').points;
  const kacRefs = useRef([]);
  const kacHeight = 440;
  const kacWidth = 10;
  const kacSize = {
    height: (kacHeight / mapOriginalHeight) * 100,
    width: (kacWidth / mapOriginalWidth) * 100,
  };

  const zaiPoints = entries.find(({id}) => id === 'zai').points;
  const zaiRefs = useRef([]);
  const zaiHeight = 70;
  const zaiWidth = 120;
  const zaiSize = {
    height: (zaiHeight / mapOriginalHeight) * 100,
    width: (zaiWidth / mapOriginalWidth) * 100,
  };

  const disiPoints = entries.find(({id}) => id === 'disi').points;
  const disiRefs = useRef([]);
  const disiHeight = 1280;
  const disiWidth = 85;
  const disiSize = {
    height: (disiHeight / mapOriginalHeight) * 100,
    width: (disiWidth / mapOriginalWidth) * 100,
  };
  const zaraPoints = entries.find(({id}) => id === 'zara').points;
  const zaraRefs = useRef([]);
  const zaraHeight = 70;
  const zaraWidth = 180;
  const zaraSize = {
    height: (zaraHeight / mapOriginalHeight) * 100,
    width: (zaraWidth / mapOriginalWidth) * 100,
  };

  useEffect(() => {
    gsap.set([kacRefs.current, zaiRefs.current, disiRefs.current], {
      transformOrigin: '50% 50%',
    });
    gsap
      .timeline({
        defaults: {
          duration: 1,
          stagger: 0.1,
          repeat: -1,
          repeatDelay: 2,
        },
      })
      .add('start')
      .fromTo(kacRefs.current, {scale: 1.75}, {scale: 0.7})
      .fromTo(zaiRefs.current, {scale: 1.75}, {scale: 0.7}, 'start+=2')
      .fromTo(disiRefs.current, {scale: 1.75}, {scale: 0.7}, 'start')
      .fromTo(zaraRefs.current, {scale: 1.75}, {scale: 0.7}, 'start');
  }, []);

  function onClick(id, position) {
    setActiveProject(id, position);
  }

  return (
    <>
      <SVG
        className={`${active ? 'active' : 'idle'}`}
        size={kacSize}
        id='kac'
        viewBox={`0 -4 ${kacWidth} ${kacHeight}`}
      >
        <g
          style={{pointerEvents: 'bounding-box'}}
          onClick={() => onClick('kac')}
          className={`conveyor ${active === 'kac' ? 'is-active' : 'is-hidden'}`}
        >
          {kacPoints.map(({x, y}, i) => (
            <circle
              ref={(el) => (kacRefs.current[i] = el)}
              key={`kac-${i}`}
              className='circle'
              cx={x}
              cy={y}
              r={`${active === 'kac' ? '4' : '3'}`}
            />
          ))}
        </g>
      </SVG>
      <SVG
        className={`${active ? 'active' : 'idle'}`}
        size={zaiSize}
        id='zai'
        viewBox={`0 -4 ${zaiWidth} ${zaiHeight}`}
      >
        <g
          style={{pointerEvents: 'bounding-box'}}
          onClick={() => onClick('zai')}
          className={`conveyor ${active === 'zai' ? 'is-active' : 'is-hidden'}`}
        >
          {zaiPoints.map(({x, y}, i) => (
            <circle
              ref={(el) => (zaiRefs.current[i] = el)}
              key={`zai-${i}`}
              className='circle'
              cx={x}
              cy={y}
              r={`${active === 'zai' ? '4' : '3'}`}
            />
          ))}
        </g>
      </SVG>
      <SVG
        size={disiSize}
        className={`${active ? 'active' : 'idle'}`}
        id='disi'
        viewBox={`0 -4 ${disiWidth} ${disiHeight}`}
      >
        <g
          style={{pointerEvents: 'bounding-box'}}
          onClick={() => onClick('disi')}
          className={`conveyor ${
            active === 'disi' ? 'is-active' : 'is-hidden'
          }`}
        >
          {disiPoints.map(({x, y}, i) => (
            <circle
              ref={(el) => (disiRefs.current[i] = el)}
              key={`disi-${i}`}
              className='circle'
              cx={x}
              cy={y}
              r={`${active === 'disi' ? '4' : '3'}`}
            />
          ))}
        </g>
      </SVG>
      <SVG
        className={`${active ? 'active' : 'idle'}`}
        size={zaraSize}
        id='zara'
        viewBox={`0 -4 ${zaraWidth} ${zaraHeight}`}
      >
        <g
          style={{pointerEvents: 'bounding-box'}}
          onClick={() => onClick('zara')}
          className={`conveyor ${
            active === 'zara' ? 'is-active' : 'is-hidden'
          }`}
        >
          {zaraPoints.map(({x, y}, i) => (
            <circle
              ref={(el) => (zaraRefs.current[i] = el)}
              key={`zara-${i}`}
              className='circle'
              cx={x}
              cy={y}
              r={`${active === 'zara' ? '4' : '3'}`}
            />
          ))}
        </g>
      </SVG>
    </>
  );
};

SVGLayer.propTypes = {
  data: PropTypes.object,
  active: PropTypes.string,
  setActiveProject: PropTypes.func,
};

export default SVGLayer;
